import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './WordModal.module.css';
import { LoaderIcon, HeartIcon, CloseIcon } from '../../../../components/Icons';
import api from '../../../../services/api';
import LoadingSpinner from '../../../../components/LoadingSpinner';

const renderBoldMarkdown = (text) => {
  if (typeof text !== 'string' || !text.includes('**')) {
    return text;
  }
  return text.split(/(\*\*[^*]+\*\*)/g).map((segment, idx) => {
    const match = segment.match(/^\*\*([\s\S]+)\*\*$/);
    if (match) {
      return <strong key={`bold-${idx}`}>{match[1]}</strong>;
    }
    return <React.Fragment key={`plain-${idx}`}>{segment}</React.Fragment>;
  });
};

const WordModal = ({
  isOpen,
  wordLookup,
  activeDict,
  dictLoading,
  dictResults,
  favoriteLoading,
  isFavorited,
  onPlayPronunciation,
  onFavoriteWord,
  onDictTabClick,
  onDefinitionClick,
  onWordLookup,
  onClose,
  className
}) => {
  const [exampleTab, setExampleTab] = useState('bilingual');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [failedAudios, setFailedAudios] = useState(new Set());
  const audioRefs = useRef({});
  const [mnemonicState, setMnemonicState] = useState({
    status: 'idle',
    content: '',
    error: '',
    word: null,
  });
  const isMnemonicTab = activeDict === 'mnemonic';
  const currentResult = !isMnemonicTab ? dictResults?.[activeDict] : null;
  const isDictLoading = !isMnemonicTab && dictLoading?.[activeDict];
  const youdaoDefinition = dictResults?.youdao?.data?.definition || (activeDict === 'youdao' ? currentResult?.data?.definition : null);
  const youdaoPhonetics = youdaoDefinition && typeof youdaoDefinition === 'object' ? youdaoDefinition.phonetics : null;
  const allowWordLookup = typeof onWordLookup === 'function';

  const phoneticEntries = (() => {
    const entries = [];
    const pushEntry = (variant, rawValue) => {
      if (typeof rawValue !== 'string') return;
      const value = rawValue.trim();
      if (!value) return;
      const isUs = variant === 'us';
      const isUk = variant === 'uk';
      entries.push({
        key: variant,
        label: isUs ? '美' : isUk ? '英' : '',
        value,
        voiceType: isUk ? 1 : 0,
      });
    };

    const resolvedPhonetics = youdaoPhonetics || wordLookup?.youdao_definition?.phonetics;

    if (resolvedPhonetics && typeof resolvedPhonetics === 'object') {
      pushEntry('us', resolvedPhonetics.us);
      pushEntry('uk', resolvedPhonetics.uk);
    }

    if (!entries.length && typeof wordLookup?.phonetic === 'string') {
      const fallback = wordLookup.phonetic.trim();
      if (fallback) {
        entries.push({
          key: 'generic',
          label: '',
          value: fallback,
          voiceType: 0,
        });
      }
    }

    return entries;
  })();

  useEffect(() => {
    if (activeDict !== 'youdao') {
      setExampleTab('bilingual');
      return;
    }
    const examples = youdaoDefinition?.examples;
    const availableTabs = [];
    if (Array.isArray(examples?.bilingual) && examples.bilingual.length > 0) {
      availableTabs.push('bilingual');
    }
    if (Array.isArray(examples?.audio) && examples.audio.length > 0) {
      availableTabs.push('audio');
    }
    if (Array.isArray(examples?.authoritative) && examples.authoritative.length > 0) {
      availableTabs.push('authoritative');
    }
    const fallback = availableTabs[0] || 'bilingual';
    setExampleTab((prev) => (availableTabs.includes(prev) ? prev : fallback));
  }, [activeDict, youdaoDefinition]);

  // 重置失败音频状态当词汇变化时
  useEffect(() => {
    setFailedAudios(new Set());
    setMnemonicState({ status: 'idle', content: '', error: '', word: null });
  }, [wordLookup?.word]);

  const fetchMnemonic = useCallback(
    async (force = false) => {
      const word = wordLookup?.word?.trim();
      if (!word) {
        setMnemonicState({ status: 'idle', content: '', error: '', word: null });
        return;
      }

      if (!force) {
        if (mnemonicState.status === 'loading' && mnemonicState.word === word) {
          return;
        }
        if (mnemonicState.status === 'success' && mnemonicState.word === word) {
          return;
        }
      }

      setMnemonicState({ status: 'loading', content: '', error: '', word });

      const payload = {
        word,
        language: 'zh',
      };

      const youdao = wordLookup?.youdao_definition;
      const translation = youdao?.translation;

      if (Array.isArray(translation) && translation.length) {
        const text = translation
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
          .join('；');
        if (text) {
          payload.definition = text;
        }
      } else if (typeof translation === 'string' && translation.trim()) {
        payload.definition = translation.trim();
      }

      const exampleCandidate = (() => {
        const examples = youdao?.examples;
        if (!examples) return null;
        if (Array.isArray(examples?.bilingual) && examples.bilingual.length) {
          const first = examples.bilingual[0];
          if (typeof first === 'string') return first;
          if (first?.en) return first.en;
        }
        if (Array.isArray(examples?.audio) && examples.audio.length) {
          const first = examples.audio[0];
          if (typeof first === 'string') return first;
          if (first?.sentence) return first.sentence;
        }
        return null;
      })();

      if (exampleCandidate && typeof exampleCandidate === 'string') {
        const trimmed = exampleCandidate.trim();
        if (trimmed) {
          payload.example = trimmed;
        }
      }

      try {
        const { data } = await api.post('/guiji-llm/mnemonic', payload);
        const mnemonicText = (data?.mnemonic || '').trim();
        if (mnemonicText) {
          setMnemonicState({ status: 'success', content: mnemonicText, error: '', word });
        } else {
          setMnemonicState({ status: 'error', content: '', error: '未生成助记内容', word });
        }
      } catch (err) {
        const message = err?.response?.data?.detail || err?.message || '生成助记失败';
        setMnemonicState({ status: 'error', content: '', error: message, word });
      }
    },
    [wordLookup, mnemonicState]
  );

  useEffect(() => {
    if (!isMnemonicTab) {
      return;
    }
    fetchMnemonic(false);
  }, [isMnemonicTab, wordLookup?.word, fetchMnemonic]);

  const handleWordLookup = useCallback(
    (rawWord) => {
      if (!allowWordLookup) return;
      const text = typeof rawWord === 'string' ? rawWord.trim() : '';
      if (!text) return;
      onWordLookup?.(text);
    },
    [allowWordLookup, onWordLookup]
  );

  const renderInteractiveText = useCallback(
    (text, keyPrefix = 'interactive') => {
      if (!text || typeof text !== 'string') {
        return text;
      }
      if (!allowWordLookup) {
        return text;
      }
      const parts = text.split(/(\b[A-Za-z][A-Za-z'-]*\b)/g);
      return parts.map((part, index) => {
        if (!part) {
          return null;
        }
        const isWord = /^[A-Za-z][A-Za-z'-]*$/.test(part);
        if (!isWord) {
          return (
            <React.Fragment key={`${keyPrefix}-text-${index}`}>
              {part}
            </React.Fragment>
          );
        }
        return (
          <span
            key={`${keyPrefix}-word-${index}`}
            className={styles.clickableWord}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleWordLookup(part);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleWordLookup(part);
              }
            }}
          >
            {part}
          </span>
        );
      });
    },
    [allowWordLookup, handleWordLookup]
  );

  const handleAudioToggle = (audioId) => {
    const audio = audioRefs.current[audioId];
    if (!audio) {
      console.error('音频元素未找到:', audioId);
      return;
    }

    if (playingAudio === audioId) {
      // 暂停当前播放的音频
      audio.pause();
      setPlayingAudio(null);
    } else {
      // 暂停其他音频并播放当前音频
      Object.entries(audioRefs.current).forEach(([id, audioEl]) => {
        if (id !== audioId && audioEl) {
          audioEl.pause();
        }
      });
      
      // 确保音频已加载后再播放
      if (audio.readyState >= 2) {
        // HAVE_CURRENT_DATA 或更高状态
        audio.play().catch((error) => {
          console.error('播放音频失败:', error);
          setPlayingAudio(null);
        });
        setPlayingAudio(audioId);
      } else {
        // 如果音频还未加载，先加载再播放
        audio.load();
        audio.play().then(() => {
          setPlayingAudio(audioId);
        }).catch((error) => {
          console.error('播放音频失败:', error, '音频URL:', audio.src);
          setPlayingAudio(null);
        });
      }
    }
  };

  const handleAudioEnded = (audioId) => {
    if (playingAudio === audioId) {
      setPlayingAudio(null);
    }
  };

  if (!isOpen || !wordLookup) {
    return null;
  }

  const renderTagChips = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return null;
    }
    return (
      <div className={styles.tagList}>
        {items.map((item, index) => (
          <span key={`${item}-${index}`} className={styles.tagChip}>
            {item}
          </span>
        ))}
      </div>
    );
  };

  const renderBilingualExamples = (examples) => {
    // 生成有道语音 URL
    const generateYoudaoVoiceUrl = (text) => {
      if (!text || typeof text !== 'string') return null;
      const encodedText = encodeURIComponent(text.trim());
      return `https://dict.youdao.com/dictvoice?audio=${encodedText}&type=2`;
    };

    return (
      <div className={styles.exampleList}>
        {examples.map((example, index) => {
          const audioId = `bilingual-audio-${index}`;
          const isPlaying = playingAudio === audioId;
          const hasFailed = failedAudios.has(audioId);
          const audioUrl = generateYoudaoVoiceUrl(example.en);
          
          return (
            <div key={index} className={styles.exampleItem}>
              <div className={styles.exampleWithAudio}>
                {example.en && (
                  <p className={styles.exampleText}>
                    {renderInteractiveText(example.en, `bilingual-${index}`)}
                  </p>
                )}
                {audioUrl && (
                  hasFailed ? (
                    <div className={styles.audioError}>
                      <span className={styles.errorIcon}>⚠️</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.inlineAudioButton} ${isPlaying ? styles.inlineAudioButtonActive : ''}`}
                      onClick={() => handleAudioToggle(audioId)}
                      aria-label={isPlaying ? '暂停语音' : '播放语音'}
                      title="播放语音"
                    >
                      <svg className={styles.smallSpeakerIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      </svg>
                    </button>
                  )
                )}
              </div>
              {example.zh && <p className={styles.exampleTranslation}>{example.zh}</p>}
              {audioUrl && (
                <audio
                  ref={(el) => {
                    if (el) audioRefs.current[audioId] = el;
                  }}
                  src={audioUrl}
                  preload="metadata"
                  onEnded={() => handleAudioEnded(audioId)}
                  onError={(e) => {
                    console.error('例句语音加载失败:', audioId, '错误代码:', e.target.error?.code);
                    if (playingAudio === audioId) {
                      setPlayingAudio(null);
                    }
                    setFailedAudios(prev => new Set([...prev, audioId]));
                  }}
                  style={{ display: 'none' }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAudioExamples = (examples) => {
    // 清理音频 URL，移除可能的错误前缀
    const cleanAudioUrl = (url) => {
      if (!url || typeof url !== 'string') return null;
      
      // 移除可能的错误前缀（例如：movie=）
      let cleanedUrl = url;
      const movieMatch = url.match(/movie=(https?:\/\/.+)$/);
      if (movieMatch) {
        cleanedUrl = movieMatch[1];
      }
      
      // 检查是否是绝对 URL
      if (!cleanedUrl.startsWith('http://') && !cleanedUrl.startsWith('https://')) {
        return null;
      }
      
      return cleanedUrl;
    };

    return (
      <div className={styles.audioExampleList}>
        {examples.map((example, index) => {
          const audioId = `audio-${index}`;
          const isPlaying = playingAudio === audioId;
          const cleanedAudioUrl = cleanAudioUrl(example.audio);
          const hasFailed = failedAudios.has(audioId);
          
          return (
            <div key={index} className={styles.audioExample}>
              <div className={styles.exampleWithAudio}>
                {example.sentence && (
                  <p className={styles.exampleText}>
                    {renderInteractiveText(example.sentence, `audio-${index}`)}
                  </p>
                )}
                {cleanedAudioUrl && (
                  hasFailed ? (
                    <div className={styles.audioError} title="音频加载失败">
                      <span className={styles.errorIcon}>⚠️</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.inlineAudioButton} ${isPlaying ? styles.inlineAudioButtonActive : ''}`}
                      onClick={() => handleAudioToggle(audioId)}
                      aria-label={isPlaying ? '暂停音频' : '播放音频'}
                      title="播放音频"
                    >
                      <svg className={styles.smallSpeakerIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      </svg>
                    </button>
                  )
                )}
              </div>
              {cleanedAudioUrl && !hasFailed && (
                <audio
                  ref={(el) => {
                    if (el) audioRefs.current[audioId] = el;
                  }}
                  src={cleanedAudioUrl}
                  preload="metadata"
                  onEnded={() => handleAudioEnded(audioId)}
                  onError={(e) => {
                    console.error('音频加载失败:', audioId, '错误代码:', e.target.error?.code, 'URL:', cleanedAudioUrl);
                    if (playingAudio === audioId) {
                      setPlayingAudio(null);
                    }
                    // 标记该音频为失败
                    setFailedAudios(prev => new Set([...prev, audioId]));
                  }}
                  style={{ display: 'none' }}
                />
              )}
              {hasFailed && (
                <div className={styles.unsupportedAudio}>
                  <span className={styles.audioHint}>
                    ⚠️ 该音频格式浏览器不支持播放，
                    <a 
                      href={cleanedAudioUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.downloadLink}
                    >
                      请前往下载
                    </a>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAuthoritativeExamples = (examples) => (
    <div className={styles.exampleList}>
      {examples.map((item, index) => (
        <div key={index} className={styles.exampleItem}>
          {item.sentence && (
            <p className={styles.exampleText}>
              {renderInteractiveText(item.sentence, `authoritative-${index}`)}
            </p>
          )}
          {item.source && <p className={styles.metaItem}>来源：{item.source}</p>}
        </div>
      ))}
    </div>
  );

  const renderSynonyms = (synonyms) => (
    <div className={styles.synonymList}>
      {synonyms.map((entry, index) => (
        <div key={index} className={styles.synonymGroup}>
          <div className={styles.entryHeader}>
            {entry.pos && <span className={styles.partOfSpeech}>{entry.pos}</span>}
            {entry.meaning && <span className={styles.metaItem}>{entry.meaning}</span>}
          </div>
          {entry.words && entry.words.length > 0 && (
            <div className={styles.tagList}>
              {entry.words.map((word, wordIndex) => (
                <span
                  key={`${word}-${wordIndex}`}
                    className={styles.tagChip}
                    onClick={() => allowWordLookup && handleWordLookup(word)}
                  role={allowWordLookup ? 'button' : undefined}
                  tabIndex={allowWordLookup ? 0 : undefined}
                  onKeyDown={(event) => {
                    if (!allowWordLookup) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleWordLookup(word);
                    }
                  }}
                >
                  {word}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderRelatedWords = (relatedWords) => (
    <div className={styles.relatedList}>
      {relatedWords.map((entry, index) => (
        <div key={index} className={styles.relatedGroup}>
          {entry.pos && <div className={styles.entryHeader}><span className={styles.partOfSpeech}>{entry.pos}</span></div>}
          {entry.words && entry.words.length > 0 && (
            <ul className={styles.bulletList}>
              {entry.words.map((wordItem, wordIndex) => (
                <li key={wordIndex}>
                  <span
                    className={`${styles.relatedWord} ${allowWordLookup ? styles.clickableWord : ''}`}
                    onClick={() => handleWordLookup(wordItem.word)}
                    role={allowWordLookup ? 'button' : undefined}
                    tabIndex={allowWordLookup ? 0 : undefined}
                    onKeyDown={(event) => {
                      if (!allowWordLookup) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleWordLookup(wordItem.word);
                      }
                    }}
                  >
                    {wordItem.word}
                  </span>
                  {wordItem.definition && <span className={styles.relatedDefinition}> - {wordItem.definition}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );

  const renderYoudaoDefinition = (definition) => {
    if (!definition) {
      return (
        <div className={styles.emptyState}>
          <p>未找到该单词的详细释义。</p>
        </div>
      );
    }
    if (typeof definition === 'string') {
      return <div dangerouslySetInnerHTML={{ __html: definition }} onClick={onDefinitionClick} />;
    }
    if (typeof definition !== 'object') {
      return (
        <div className={styles.youdaoRaw}>
          <pre>{JSON.stringify(definition, null, 2)}</pre>
        </div>
      );
    }

    const {
      exam_types: examTypes,
      variants,
      definitions = {},
      phrases,
      examples = {},
      synonyms,
      related_words: relatedWords,
      etymology,
      baike,
    } = definition || {};

    const hasSimpleDefinitions = Array.isArray(definitions?.simple) && definitions.simple.length > 0;
    const hasCollinsDefinitions = Array.isArray(definitions?.collins) && definitions.collins.length > 0;
    const hasWebDefinitions = Array.isArray(definitions?.web) && definitions.web.length > 0;
    const hasSpecialistDefinitions = Array.isArray(definitions?.specialist) && definitions.specialist.length > 0;
    const hasEnglishDefinitions = Array.isArray(definitions?.english) && definitions.english.length > 0;
    const hasPhrases = Array.isArray(phrases) && phrases.length > 0;
    const hasBilingualExamples = Array.isArray(examples?.bilingual) && examples.bilingual.length > 0;
    const hasAudioExamples = Array.isArray(examples?.audio) && examples.audio.length > 0;
    const hasAuthoritativeExamples = Array.isArray(examples?.authoritative) && examples.authoritative.length > 0;
    const hasSynonyms = Array.isArray(synonyms) && synonyms.length > 0;
    const hasRelatedWords = Array.isArray(relatedWords) && relatedWords.length > 0;
    const hasEtymology = Array.isArray(etymology) && etymology.length > 0;
    const hasBaike = !!(
      baike && (
        (Array.isArray(baike.summaries) && baike.summaries.length > 0) ||
        baike.source
      )
    );

    const sections = [];
    if (hasSimpleDefinitions) {
      sections.push(
        <div key="simple" className={styles.youdaoSection}>
          <h4 className={styles.sectionTitle}>基础释义</h4>
          <ul className={styles.simpleList}>
            {definitions.simple.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          {renderTagChips(examTypes)}

        </div>
      );
    }
    // if (Array.isArray(examTypes) && examTypes.length > 0) {
    //   sections.push(
    //     <div key="exam-types" className={styles.youdaoSection}>
    //       <h4 className={styles.sectionTitle}>考试类型</h4>
    //       {renderTagChips(examTypes)}
    //     </div>
    //   );
    // }

    if (Array.isArray(variants) && variants.length > 0) {
      sections.push(
        <div key="variants" className={styles.youdaoSection}>
          <h4 className={styles.sectionTitle}>词形变化</h4>
            <div className={styles.variantGrid}>
            {variants.map((variant, index) => (
                <div key={index} className={styles.variantCard}>
                  {variant.label && <span className={styles.variantLabel}>{variant.label}</span>}
                  <span className={styles.variantValue}>
                {renderInteractiveText(variant.value, `variant-${index}`)}
                  </span>
                </div>
            ))}
            </div>
        </div>
      );
    }



    // if (hasCollinsDefinitions) {
    //   sections.push(
    //     <div key="collins" className={styles.youdaoSection}>
    //       <h4 className={styles.sectionTitle}>柯林斯释义</h4>
    //       {definitions.collins.map((entry, index) => (
    //         <div key={index} className={styles.definitionGroup}>
    //           <div className={styles.entryHeader}>
    //             {entry.pos && <span className={styles.partOfSpeech}>{entry.pos}</span>}
    //             {entry.grammar && <span className={styles.grammarTag}>{entry.grammar}</span>}
    //           </div>
    //           {entry.definition && <p className={styles.definitionText}>{entry.definition}</p>}
    //           {Array.isArray(entry.examples) && entry.examples.length > 0 && (
    //             <div className={styles.exampleList}>
    //               {entry.examples.map((example, exIndex) => (
    //                 <div key={exIndex} className={styles.exampleItem}>
    //                   {example.en && <p className={styles.exampleText}>{example.en}</p>}
    //                   {example.zh && <p className={styles.exampleTranslation}>{example.zh}</p>}
    //                 </div>
    //               ))}
    //             </div>
    //           )}
    //         </div>
    //       ))}
    //     </div>
    //   );
    // }

    // if (hasEnglishDefinitions) {
    //   sections.push(
    //     <div key="english-defs" className={styles.youdaoSection}>
    //       <h4 className={styles.sectionTitle}>英文释义</h4>
    //       {definitions.english.map((entry, index) => (
    //         <div key={index} className={styles.definitionGroup}>
    //           <div className={styles.entryHeader}>
    //             {entry.pos && <span className={styles.partOfSpeech}>{entry.pos}</span>}
    //             {Array.isArray(entry.similar_words) && entry.similar_words.length > 0 && (
    //               <span className={styles.metaItem}>
    //                 近义词：{entry.similar_words.join('、')}
    //               </span>
    //             )}
    //           </div>
    //           {entry.definition && <p className={styles.definitionText}>{entry.definition}</p>}
    //           {Array.isArray(entry.examples) && entry.examples.length > 0 && (
    //             <div className={styles.exampleList}>
    //               {entry.examples.map((example, exIndex) => (
    //                 <div key={exIndex} className={styles.exampleItem}>
    //                   <p className={styles.exampleText}>{example}</p>
    //                 </div>
    //               ))}
    //             </div>
    //           )}
    //         </div>
    //       ))}
    //     </div>
    //   );
    // }

    // if (hasWebDefinitions) {
    //   sections.push(
    //     <div key="web" className={styles.youdaoSection}>
    //       <h4 className={styles.sectionTitle}>网络释义</h4>
    //       {definitions.web.map((entry, index) => (
    //         <div key={index} className={styles.definitionGroup}>
    //           {entry.term && <p className={styles.definitionTerm}>{entry.term}</p>}
    //           {Array.isArray(entry.translations) && entry.translations.length > 0 && renderTagChips(entry.translations)}
    //           {Array.isArray(entry.examples) && entry.examples.length > 0 && (
    //             <div className={styles.exampleList}>
    //               {entry.examples.map((example, exIndex) => (
    //                 <div key={exIndex} className={styles.exampleItem}>
    //                   <p className={styles.exampleText}>{example}</p>
    //                 </div>
    //               ))}
    //             </div>
    //           )}
    //         </div>
    //       ))}
    //     </div>
    //   );
    // }

    // if (hasSpecialistDefinitions) {
    //   sections.push(
    //     <div key="specialist" className={styles.youdaoSection}>
    //       <h4 className={styles.sectionTitle}>专业释义</h4>
    //       {definitions.specialist.map((entry, index) => (
    //         <div key={index} className={styles.definitionGroup}>
    //           <div className={styles.entryHeader}>
    //             {entry.field && <span className={styles.grammarTag}>{entry.field}</span>}
    //             {entry.term && <span className={styles.definitionTerm}>{entry.term}</span>}
    //           </div>
    //           {entry.english && <p className={styles.definitionText}>{entry.english}</p>}
    //           {entry.chinese && <p className={styles.exampleTranslation}>{entry.chinese}</p>}
    //           {entry.source && <p className={styles.metaItem}>来源：{entry.source}</p>}
    //         </div>
    //       ))}
    //     </div>
    //   );
    // }

    if (hasPhrases) {
      sections.push(
        <div key="phrases" className={styles.youdaoSection}>
          <h4 className={styles.sectionTitle}>短语搭配</h4>
          <div className={styles.phraseList}>
          {phrases.map((phrase, index) => (
              <div key={index} className={`${styles.definitionGroup} ${styles.phraseGroup}`}>
                <div className={styles.phraseMain}>
              {phrase.term && (
                <p className={styles.definitionTerm}>
                  {renderInteractiveText(phrase.term, `phrase-term-${index}`)}
                </p>
              )}
                  {Array.isArray(phrase.translations) && phrase.translations.length > 0 && (
                    <div className={styles.phraseTranslations}>
                      {phrase.translations.map((t, tIdx) => (
                        <span key={tIdx} className={styles.phraseTranslation}>
                          {t}
                          {tIdx < phrase.translations.length - 1 ? '；' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              {Array.isArray(phrase.examples) && phrase.examples.length > 0 && (
                  <div className={styles.phraseExamples}>
                  {phrase.examples.map((example, exIndex) => (
                      <div key={exIndex} className={styles.phraseExample}>
                        {renderInteractiveText(example, `phrase-example-${index}-${exIndex}`)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div>
        </div>
      );
    }

    const exampleTabsData = [
      hasBilingualExamples && { key: 'bilingual', label: '双语例句' },
      hasAudioExamples && { key: 'audio', label: '音频例句' },
      hasAuthoritativeExamples && { key: 'authoritative', label: '权威例句' },
    ].filter(Boolean);

    if (exampleTabsData.length) {
      sections.push(
        <div key="examples" className={styles.youdaoSection}>
          <div className={styles.examplesHeader}>
            <h4 className={styles.sectionTitle}>例句</h4>
            <div className={styles.exampleTabs}>
              {exampleTabsData.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`${styles.exampleTab} ${exampleTab === tab.key ? styles.exampleTabActive : ''}`}
                  onClick={() => setExampleTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.examplesContent}>
            {exampleTab === 'bilingual' && hasBilingualExamples && renderBilingualExamples(examples.bilingual)}
            {exampleTab === 'audio' && hasAudioExamples && renderAudioExamples(examples.audio)}
            {exampleTab === 'authoritative' && hasAuthoritativeExamples && renderAuthoritativeExamples(examples.authoritative)}
          </div>
        </div>
      );
    }

    if (hasSynonyms) {
      sections.push(
        <div key="synonyms" className={styles.youdaoSection}>
          <h4 className={styles.sectionTitle}>近义词</h4>
          {renderSynonyms(synonyms)}
        </div>
      );
    }

    if (hasRelatedWords) {
      sections.push(
        <div key="related-words" className={styles.youdaoSection}>
          <h4 className={styles.sectionTitle}>相关词</h4>
          {renderRelatedWords(relatedWords)}
        </div>
      );
    }

    if (hasEtymology) {
      sections.push(
        <div key="etymology" className={styles.youdaoSection}>
          <h4 className={styles.sectionTitle}>词源</h4>
          <div className={styles.etymologyList}>
            {etymology.map((item, index) => (
              <div key={index} className={styles.etymologyItem}>
                {item.word && <p className={styles.definitionTerm}>{item.word}</p>}
                {item.description && <p className={styles.definitionText}>{item.description}</p>}
                {(item.source || item.url) && (
                  <p className={styles.metaItem}>
                    来源：{item.source}
                    {item.url && (
                      <>
                        {' '}
                        <a href={item.url} target="_blank" rel="noreferrer">{item.url}</a>
                      </>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // if (hasBaike) {
    //   sections.push(
    //     <div key="baike" className={styles.youdaoSection}>
    //       <h4 className={styles.sectionTitle}>百科</h4>
    //       {Array.isArray(baike.summaries) && baike.summaries.length > 0 && (
    //         <div className={styles.exampleList}>
    //           {baike.summaries.map((summary, index) => (
    //             <div key={index} className={styles.exampleItem}>
    //               <p className={styles.definitionText}>{summary}</p>
    //             </div>
    //           ))}
    //         </div>
    //       )}
    //       {baike.source && (
    //         <p className={styles.metaItem}>
    //           来源：
    //           {baike.source.url ? (
    //             <a href={baike.source.url} target="_blank" rel="noreferrer">{baike.source.name || baike.source.url}</a>
    //           ) : (
    //             baike.source.name
    //           )}
    //         </p>
    //       )}
    //     </div>
    //   );
    // }

    if (!sections.length) {
      return (
        <pre className={styles.youdaoRaw}>{JSON.stringify(definition, null, 2)}</pre>
      );
    }

    return <div className={styles.youdaoContent}>{sections}</div>;
  };

  const renderMnemonicContent = () => {
    const word = wordLookup?.word?.trim();
    if (!word) {
      return (
        <div className={styles.emptyState}>
          <p>选择单词后，可生成 AI 助记提示。</p>
        </div>
      );
    }

    if (mnemonicState.status === 'loading') {
      return (
        <div className={styles.loading}>
          <LoadingSpinner size="small" text="正在生成助记提示..." />
        </div>
      );
    }

    if (mnemonicState.status === 'success') {
      return (
        <div className={styles.mnemonicSection}>
          <div className={styles.mnemonicHeader}>
            <span className={styles.mnemonicTitle}>AI 助记</span>
            <button
              type="button"
              className={styles.mnemonicButton}
              onClick={() => fetchMnemonic(true)}
            >
              <span className="material-icons-round" style={{ fontSize: '16px', marginRight: '6px', verticalAlign: 'middle' }}>
                refresh
              </span>
              重新生成
            </button>
          </div>
          <p className={styles.mnemonicContent}>{renderBoldMarkdown(mnemonicState.content)}</p>
        </div>
      );
    }

    if (mnemonicState.status === 'error') {
      return (
        <div className={styles.mnemonicSection}>
          <p className={styles.mnemonicError}>生成失败：{mnemonicState.error}</p>
          <div className={styles.mnemonicActions}>
            <button
              type="button"
              className={styles.mnemonicButton}
              onClick={() => fetchMnemonic(true)}
            >
              重新生成
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.mnemonicSection}>
        <p className={styles.mnemonicHint}>点击下方按钮生成助记提示，帮助更快记忆该单词。</p>
        <div className={styles.mnemonicActions}>
          <button
            type="button"
            className={styles.mnemonicButton}
            onClick={() => fetchMnemonic(true)}
          >
            生成助记
          </button>
        </div>
      </div>
    );
  };

  const modalContent = (
    <div
      className={`${styles.overlay} ${className || ''}`}
      onClick={() => onClose?.()}
      role="presentation"
    >
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => onClose?.()}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.titleSection}>
            <div className={styles.titleRowPrimary}>
              <div className={styles.wordWithAction}>
              <h2 className={styles.word}>{wordLookup.word}</h2>
              <button
                className={`${styles.favoriteButton} ${isFavorited ? styles.favoriteButtonActive : ''}`}
                onClick={onFavoriteWord}
                disabled={favoriteLoading || !wordLookup.success}
                title={isFavorited ? '取消收藏' : '收藏单词'}
                aria-label={isFavorited ? '取消收藏' : '收藏单词'}
              >
                {favoriteLoading ? (
                    <LoaderIcon size={20} />
                ) : (
                    <HeartIcon size={24} filled={isFavorited} />
                )}
              </button>
              </div>

            </div>

            {phoneticEntries.length > 0 && (
              <div className={styles.titleRowSecondary}>
                <div className={styles.phoneticGroup}>
                  {phoneticEntries.map(({ key, label, value, voiceType }) => (
                    <div key={key} className={styles.phoneticItem}>
                      <span className={styles.phoneticValue}>
                        {label ? `${label} ${value}` : value}
                      </span>
                      {onPlayPronunciation && (
                        <button
                          className={styles.pronounceButton}
                          onClick={() => onPlayPronunciation?.(voiceType)}
                          title={label ? `播放${label}发音` : '播放发音'}
                        >
                          {/* {label || '播放'} */}
                          <svg className={styles.speakerIcon} viewBox="0 0 16 16" aria-hidden="true">
                            <path d="M3 6.5v3h2.5l3 2V4.5l-3 2H3z" fill="currentColor" />
                            <path
                              d="M10.5 5.25a2.75 2.75 0 010 5.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.1"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={styles.dictTabs}>
            <button
              className={`${styles.dictTab} ${activeDict === 'youdao' ? styles.dictTabActive : ''}`}
              onClick={() => onDictTabClick?.('youdao')}
            >
              Youdao
              {dictLoading?.youdao && <LoaderIcon size={14} />}
            </button>
            <button
              className={`${styles.dictTab} ${activeDict === 'collins' ? styles.dictTabActive : ''}`}
              onClick={() => onDictTabClick?.('collins')}
            >
              Collins
              {dictLoading?.collins && <LoaderIcon size={14} />}
            </button>
            <button
              className={`${styles.dictTab} ${activeDict === 'mnemonic' ? styles.dictTabActive : ''}`}
              onClick={() => onDictTabClick?.('mnemonic')}
              type="button"
            >
              AI 助记
              {mnemonicState.status === 'loading' && <LoaderIcon size={14} />}
            </button>
          </div>

          <div className={styles.dictContent}>
            {isMnemonicTab ? (
              renderMnemonicContent()
            ) : isDictLoading ? (
              <div className={styles.loading}>
                <LoadingSpinner size="small" text={`正在查询${activeDict === 'collins' ? 'Collins' : 'EDICT'}字典...`} />
              </div>
            ) : currentResult ? (
              currentResult.success ? (
                <div className={styles.wordResult}>
                  <div className={styles.dictionaryItem}>
                    {activeDict === 'youdao'
                      ? renderYoudaoDefinition(currentResult.data.definition)
                      : (
                        <div
                          className={styles.dictionaryDefinition}
                          dangerouslySetInnerHTML={{ __html: currentResult.data.definition }}
                          onClick={onDefinitionClick}
                        />
                      )}
                  </div>
                </div>
              ) : (
                <div className={styles.errorResult}>
                  {/* <h3>查询失败</h3> */}
                  <p>{'此字典未收录此单词'}</p>
                  {activeDict === 'youdao' && (
                    <div className={styles.suggestion}>
                      <p className={styles.suggestionTitle}>建议：</p>
                      <ul className={styles.suggestionList}>
                        <li>检查单词拼写是否正确</li>
                        <li>尝试单词的其他形式（原形、复数等）</li>
                        <li>该单词可能是专有名词或较新的词汇</li>
                      </ul>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className={styles.emptyState}>
                <p>点击上方标签查看字典释义</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default WordModal;
